import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LoginService } from '../../services/login-service/login.service';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  enableLoginPart: boolean = false;
  isLoginButtonEnabled: boolean = true;
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  selectedNode: string = '';
  selectedEnv: string = '';
  isLoading: boolean = false;

  // Use nodeScopes from environment
  private nodeScopes = environment.nodeScopes;

  constructor(
    private router: Router,
    private loginService: LoginService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public toastService: ToastrService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loginService.loaderSubject.subscribe((response: any) => {
      if (response) {
        this.isLoading = true;
      } else {
        this.isLoading = false;
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      const savedNode = sessionStorage.getItem('selectedNode');
      if (savedNode === 'Select Node') {
        sessionStorage.removeItem('selectedNode');
      }
      const sessionId = sessionStorage.getItem('Session_Id');
      const returnUrl = this.router.parseUrl(this.router.url).queryParams[
        'returnUrl'
      ];

      if (sessionId && returnUrl) {
        this.router.navigateByUrl(returnUrl);
      } else if (sessionId) {
        this.router.navigate(['/dashboard']);
      }

      this.isIframe = window !== window.parent && !window.opener;
      try {
        this.setupMsalListeners();
        this.authService.handleRedirectCallback();
      } catch (error) {
        this.toastService.error(`Error during Login Initialization: ${error}`);
      }
    }
  }

  private setupMsalListeners(): void {
    this.msalService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.setLoginDisplay();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
    this.cdRef.detectChanges();
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  onNodeSelect(event: MatSelectChange) {
    if (this.selectedNode == '' || this.selectedEnv == '') {
      this.isLoginButtonEnabled = true;
    } else {
      this.isLoginButtonEnabled = false;
    }
  }

  onEnvSelect(event: MatSelectChange) {
    if (this.selectedNode == '' || this.selectedEnv == '') {
      this.isLoginButtonEnabled = true;
    } else {
      this.isLoginButtonEnabled = false;
    }
  }

  loginRedirect() {
    sessionStorage.setItem('selectedEnv', this.selectedEnv);
    sessionStorage.setItem('selectedNode', this.selectedNode);

    // Use scopes from environment configuration
    const request: RedirectRequest = {
      scopes:
        this.nodeScopes[this.selectedNode as keyof typeof this.nodeScopes],
      extraScopesToConsent: [],
    };

    this.msalService.loginRedirect(request);
  }

  // Helper method to get available nodes (useful for dropdowns)
  getAvailableNodes(): string[] {
    return Object.keys(environment.nodeScopes);
  }

  // Helper method to get scopes for a specific node
  getScopesForNode(node: string): string[] {
    return (
      environment.nodeScopes[node as keyof typeof environment.nodeScopes] || []
    );
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  openLoginPart() {
    this.enableLoginPart = true;
  }
}
